import { Component, OnInit, AfterViewInit, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, FormControl, ValidationErrors, ControlContainer, NgForm } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ExcelImportService } from '../_services/excel-import.service';
import { InjectionService } from '../_services/injection.service';
import { UserWellsService } from '../user-wells.service';
import { WellDetailsModel } from '../_models/well-details-model';
import { InjectionDataRecord } from '../_models/injection-data-record';
import { InjectionDataBatch } from '../_models/injection-data-batch';
import { UserSubmittableDataBatch } from '../_models/user-submittable-data-batch';

import { InjectionDataGrid2Component } from '../injection-data-grid2/injection-data-grid2.component';
import { ApiResultErrorDialogComponent } from '../api-result-error-dialog/api-result-error-dialog.component';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { ImportExcelDataComponent } from '../import-excel-data/import-excel-data.component';

import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { NgDynamicBreadcrumbService } from 'ng-dynamic-breadcrumb';
import { MonthlyDateRangeSelectorComponent } from '../monthly-date-range-selector/monthly-date-range-selector.component';

@Component({
  selector: 'app-add-injection2-monthly',
  templateUrl: './add-injection2-monthly.component.html',
  styleUrls: ['./add-injection2-monthly.component.css']
})
export class AddInjection2MonthlyComponent implements OnInit {

  wellRecord: WellDetailsModel;

  excelSubmitted: boolean = false;

  wellId: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  beginDate: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
  endDate: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);

  submitBatchSubscription: Subscription;
  currentRecordsSubscription: Subscription;
  detailsWellSubscription: Subscription;
  wellIdSubscription: Subscription;
  beginDateSubscription: Subscription;
  endDateSubscription: Subscription;
  excelTemplateDataSubscription: Subscription;

  @ViewChild("dateRangeSelector") dateRangeSelector: MonthlyDateRangeSelectorComponent;
  @ViewChild("injectionDataGrid") injectionDataGrid: InjectionDataGrid2Component;
  @ViewChild("injectionIntervalDataGrid") injectionIntervalDataGrid: InjectionDataGrid2Component;
  @ViewChild("compositionDataGrid") compositionDataGrid: InjectionDataGrid2Component;

  //Forms - nominally, one for each page.
  currentForm: FormGroup;
  dateBoundsSelectionForm: FormGroup;
  useOldDataForm: FormGroup;
  injectionDataForm: FormGroup;
  injectionIntervalDataForm: FormGroup;
  compositionDataForm: FormGroup;

  //Column sets for each page's data grid, pulled from injectionService

  //This one is the first page - injection volumes
  public injectionVolumeDataColumns: any = {};

  //This one is the second page, injection interval, formations, etc.
  public injectionIntervalDataColumns: any = {};

  //This one is the third page - composition of the injected material.
  public compositionDataColumns: any = {};

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    public userWellService: UserWellsService,
    public injectionService: InjectionService,
    protected router: Router,
    protected actRoute: ActivatedRoute,
    protected breadcrumbService: NgDynamicBreadcrumbService,
    protected dialog: MatDialog,
    protected excelImportService: ExcelImportService
  ) {

    //Subscribe to the details well on the user service.
    this.detailsWellSubscription = this.userWellService.detailsWell.subscribe((x) => {

      this.wellRecord = x;

      if (this.dateBoundsSelectionForm != null) {
        var dbsForm = this.dateBoundsSelectionForm.get("dateBoundsSelectionForm");

        if (dbsForm != null) {
          dbsForm.get("WellRecord").setValue(x);
        }
      }

      if (x != null) {
        //Update the breadcrumbs to show the well UIC number.

        var breadcrumbdata = {
          Uicnumber: x.Uicnumber.toString()
        };

        this.breadcrumbService.updateBreadcrumbLabels(breadcrumbdata);

        this.updateFormationIdColumnOptions();
      }
    });

    //Subscribe to the wellId change event...
    this.wellIdSubscription = this.wellId.subscribe((newWellId) => {
      this.userWellService.LoadDetailsWell(newWellId);
    });

    //Subscribe to the result parameter on the injection service.
    this.submitBatchSubscription = this.injectionService.submitBatchResult.subscribe((x) => {

      if (x != null) {
        //If we successfully uploaded our injection data, navigate back to the well details page
        if (x.Success == true) {

          var successRoute = 'well/' + this.wellId.value;

          router.navigate([successRoute]);
        }
        else {
          var dialogRef = this.dialog.open(ApiResultErrorDialogComponent, {
            data: x
          });
        }
      }
    });

    this.excelTemplateDataSubscription = this.excelImportService.currentExcelTemplateData.subscribe((x: any) => {

      if (x != null && this.excelSubmitted) {

        let excelInputErrorList = [];

        x.Injections.forEach((injection: any, index) => {
          if (this.checkDatesMatch(injection.InjectionDate, this.injectionDataForm.value.InjectionRows[index].InjectionDate)) {
            injection = this.transformExcelInjectionForDataForm(injection);
          } else {
            excelInputErrorList.push(`Invalid date provided: ${injection.InjectionDate}. Please do not modify the date columns.`);
          }
        });

        if (excelInputErrorList.length == 0) {
          x.Injections.forEach((injection: any, index) => {
            this.injectionDataGrid.patchValues(injection, index);
            this.injectionIntervalDataGrid.patchValues(injection, index);
            this.compositionDataGrid.patchValues(injection, index);

            //additional sync is needed for manyToMany input
            this.injectionIntervalDataGrid.syncManyToManyOptions('FormationIds', index);
          });
        } else {
          let excelErrorObj = {
            Reasons: excelInputErrorList
          }
          this.dialog.open(ApiResultErrorDialogComponent, { data: excelErrorObj });
        }

      }

    });
  }

  ngOnInit(): void {

    this.injectionService.getDataColumnsListPromise().then((result: any) => {
      //if any dataColumn has a monthlyScaleFactor key, scale the min and max with that value
      result = result.map(dataColumn => {
        Object.keys(dataColumn).forEach(key => {
          let col = dataColumn[key];
          Object.keys(col).forEach(col_key => {
            if (col_key == "monthlyScaleFactor") {
              col.min *= col.monthlyScaleFactor;
              col.max *= col.monthlyScaleFactor;
            }
          });
        });
        return dataColumn
      });
      [this.injectionVolumeDataColumns, this.injectionIntervalDataColumns, this.compositionDataColumns] = result;
    }).catch((error) => {
      //display error dialog and return user to mywells when call fails
      this.dialog.open(MessageDialogComponent, {
        data: {
          title: "Service Unavailable",
          message: "Something went wrong, please try again."
        }
      }).afterClosed().subscribe(() => {
        this.router.navigate(['/mywells']);
      });
    });

    //Create the date bounds form - this is the formgroup for the first page
    this.dateBoundsSelectionForm = this.formBuilder.group({
    });

    this.useOldDataForm = this.formBuilder.group({
      'useOldInjectionValues': [true, []]
    });

    //Subscribe to the current injection batch
    this.currentRecordsSubscription = this.injectionService.currentRecords.subscribe((newInjectionDataBatch: InjectionDataBatch) => {

      if (newInjectionDataBatch != null) {
        //When we have a 
      }
    });
  }

  ngAfterViewInit(): void {
    //We need our whole form to be initialized before we do this, because all of it relies on this.
    this.wellId.next(this.actRoute.snapshot.params.wellId);

    this.initDateBoundsSelectionForm();
    this.currentForm = this.dateBoundsSelectionForm;

    this.initInjectionDataForm();
    this.initInjectionIntervalDataForm();
    this.initCompositionDataForm();

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.submitBatchSubscription != null) {
      this.submitBatchSubscription.unsubscribe();
    }

    if (this.currentRecordsSubscription != null) {
      this.currentRecordsSubscription.unsubscribe();
    }

    if (this.detailsWellSubscription != null) {
      this.detailsWellSubscription.unsubscribe();
    }

    if (this.wellIdSubscription != null) {
      this.wellIdSubscription.unsubscribe();
    }

    if (this.beginDateSubscription != null) {
      this.beginDateSubscription.unsubscribe();
    }

    if (this.endDateSubscription != null) {
      this.endDateSubscription.unsubscribe();
    }

    if (this.excelTemplateDataSubscription != null) {
      this.excelTemplateDataSubscription.unsubscribe();
    }
    this.injectionService.currentBatch = new BehaviorSubject<UserSubmittableDataBatch>(null);
  }

  initDateBoundsSelectionForm() {
    this.dateBoundsSelectionForm.addControl('dateBoundsSelectionForm', this.dateRangeSelector.dateBoundsSelectionForm);

    this.beginDateSubscription = this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("BeginDate").valueChanges.subscribe((newvalue) => {
      this.beginDate.next(newvalue);
    });

    this.endDateSubscription = this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("EndDate").valueChanges.subscribe((newvalue) => {
      this.endDate.next(newvalue);
    });

    //We have to manually grab these values initially.
    this.beginDate.next(this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("BeginDate").value);
    this.endDate.next(this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("EndDate").value);

    //Load the last injection record for this well, if one exists, 
    this.injectionService.GetLastInjectionRecordWithWellId(this.wellId.value).then((injRec: InjectionDataRecord) => {
      this.importOtherDataFromInjectionRecord(injRec);
    },
      (err: any) => {
      });

  }

  initInjectionDataForm() {
    this.injectionDataForm = this.injectionDataGrid.injectionForm;
  }

  initInjectionIntervalDataForm() {
    this.injectionIntervalDataForm = this.injectionIntervalDataGrid.injectionForm;
  }

  initCompositionDataForm() {
    this.compositionDataForm = this.compositionDataGrid.injectionForm;
  }

  importOtherDataFromInjectionRecord(injRec: InjectionDataRecord) {
  }

  //Handle the stepper changing from one step to another.
  onStepChange(event: any) {

    let stepperFormGroups = [this.dateBoundsSelectionForm, this.injectionDataForm, this.injectionIntervalDataForm];
    this.currentForm = stepperFormGroups[event.selectedIndex];

    //Are we switching away from the date selection page (index 0)?
    if (event.previouslySelectedIndex == 0) {
      this.initMonthlyInjectionBatch();
    }

    //Are we switching to the submit page?
    if (event.selectedIndex == 4) {
      this.doPresubmit();
    }

  }

  initMonthlyInjectionBatch() {

    if (this.injectionService.currentInjectionBatch.value == null || this.dateBoundsSelectionForm.dirty || this.useOldDataForm.dirty) {
      let dateBoundsFormResult = this.dateBoundsSelectionForm.value.dateBoundsSelectionForm;

      let bd = dateBoundsFormResult.BeginDate;
      let ed = dateBoundsFormResult.EndDate;

      let bUseOldInjectionValues = this.useOldDataForm.value.useOldInjectionValues;

      this.injectionService.GetEmptyMonthlySubmittableBatch(this.wellId.value, bd, ed, bUseOldInjectionValues);

      this.dateBoundsSelectionForm.markAsPristine();
      this.useOldDataForm.markAsPristine();
    }
  }

  updateFormationIdColumnOptions() {
    //Update the formation options
    this.injectionIntervalDataColumns.FormationIds.options = new Array<any>();

    if (this.wellRecord != null) {
      this.wellRecord.Formations.forEach((formation) => {
        let option = {
          value: formation.Id,
          label: formation.Name
        }

        this.injectionIntervalDataColumns.FormationIds.options.push(option);
      });
    }
  }

  fillInjectionWithFormContents(inj: InjectionDataRecord, i: number) {

    //Capture the injection data values from the form
    let injectionDataFormValues = this.injectionDataGrid.getValues();

    if (injectionDataFormValues.InjectionRows[i] != null) {
      let rowData = injectionDataFormValues.InjectionRows[i];

      let keys = Object.keys(rowData);

      keys.forEach((key) => {

        if (key != "InjectionDate") {
          inj[key] = rowData[key];
        }
      });
    }

    //Capture the injection interval form values
    let injectionIntervalDataFormValues = this.injectionIntervalDataGrid.getValues();

    if (injectionIntervalDataFormValues.InjectionRows[i] != null) {
      let rowData = injectionIntervalDataFormValues.InjectionRows[i];

      let keys = Object.keys(rowData);

      keys.forEach((key) => {

        if (key != "InjectionDate") {
          inj[key] = rowData[key];
        }
      });
    }

    //Capture the composition data form values
    let compositionDataFormValues = this.compositionDataGrid.getValues();

    if (compositionDataFormValues.InjectionRows[i] != null) {
      let rowData = compositionDataFormValues.InjectionRows[i];

      let keys = Object.keys(rowData);

      keys.forEach((key) => {

        if (key != "InjectionDate") {
          inj[key] = rowData[key];
        }
      });
    }
  }

  //Extra data transformation steps are needed for more complex user input
  transformExcelInjectionForDataForm(injection) {
    if (injection.InjectionThrough) {
      injection.InjectionThrough = injection.InjectionThrough.toLowerCase();
    }

    if (injection.InjectedFluidsOtherSources !== undefined) {
      if ((typeof injection.InjectedFluidsOtherSources == 'string' && injection.InjectedFluidsOtherSources.toLowerCase() == 'yes') || injection.InjectedFluidsOtherSources === true) {
        injection.InjectedFluidsOtherSources = true;
      } else {
        injection.InjectedFluidsOtherSources = null;
      }
    }

    if (injection.FormationIds) {
      let formationNameList = injection.FormationIds.toUpperCase().split(',');

      //Moving the removal of spaces to after the split operation so that they don't get removed from the middle of names.
      for (let i = 0; i < formationNameList.length; i++) {
        formationNameList[i] = formationNameList[i].trim();
      }

      let matchedFormationIdList = [];
      this.injectionIntervalDataColumns.FormationIds.options.forEach(option => {
        if (formationNameList.includes(option.label)) {
          let matchedIndex = formationNameList.indexOf(option.label);
          formationNameList.splice(matchedIndex, 1);
          matchedFormationIdList.push(option.value);
        }
      });
      injection.FormationIds = matchedFormationIdList.join(',');
    }

    return injection;
  }

  checkDatesMatch(date1, date2) {
    return new Date(date1).getTime() == new Date(date2).getTime();
  }

  async getExcelTemplate() {
    let dateBoundsFormResult = this.dateBoundsSelectionForm.value.dateBoundsSelectionForm;

    let combinedDataForms = []
    for (let i = 0; i < this.injectionDataForm.value.InjectionRows.length; i++) {
      combinedDataForms.push({ ...this.injectionDataForm.value.InjectionRows[i], ...this.injectionIntervalDataForm.value.InjectionRows[i], ...this.compositionDataForm.value.InjectionRows[i] })
    }

    let dataColumnsList = await this.injectionService.getCombinedDataColumnsList();

    this.excelImportService.generateTemplate(this.wellRecord, dateBoundsFormResult.BeginDate, dateBoundsFormResult.EndDate, 'monthly', 'InjectionVolume', dataColumnsList, combinedDataForms);
  }

  async uploadExcelTemplate() {

    let dataColumnslist = await this.injectionService.getCombinedDataColumnsList();
    let config: any = {};

    config.data = {
      dataColumnsList: dataColumnslist,
      controllerName: "injection"
    };

    this.dialog.open(ImportExcelDataComponent, config);
    this.excelSubmitted = true;
  }

  doPresubmit() {
    //Avoid a dumb crash, in case the injection batch is null for some reason.
    if (this.injectionService.currentBatch.value != null) {
      let curBatch: InjectionDataBatch = <InjectionDataBatch>this.injectionService.currentBatch.value;

      //We're not going to use forEach here, because we need the index as a variable anyway.
      for (let i = 0; i < curBatch.Injections.length; i++) {
        let inj = <InjectionDataRecord>curBatch.Injections[i];

        //Fill the injection record with data from the forms.
        this.fillInjectionWithFormContents(inj, i);
      }

      //Do the presubmit validation
      this.injectionService.doPresubmitValidation(this.injectionService.currentBatch.value);
    }
  }

  doSubmit() {

    let batch = this.injectionService.currentBatch.value;

    if (batch != null) {

      this.injectionService.SubmitBatch(batch);
    }
  }

  goNext(stepper: MatStepper) {
    stepper.next();
  }

  goBack(stepper: MatStepper) {
    stepper.previous();
  }

}
